import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const QuickPublishForm = ({ onPublish, className = '' }) => {
  const [formData, setFormData] = useState({
    title: '',
    originalPrice: '',
    discountedPrice: '',
    category: '',
    description: '',
    pickupTime: '',
    quantity: 1,
    dietaryInfo: [],
    images: []
  });
  const [isPublishing, setIsPublishing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const categoryOptions = [
    { value: 'bakery', label: 'Panadería' },
    { value: 'restaurant', label: 'Restaurante' },
    { value: 'cafe', label: 'Cafetería' },
    { value: 'grocery', label: 'Supermercado' },
    { value: 'desserts', label: 'Postres' }
  ];

  const dietaryOptions = [
    { value: 'vegetarian', label: 'Vegetariano' },
    { value: 'vegan', label: 'Vegano' },
    { value: 'gluten-free', label: 'Sin Gluten' },
    { value: 'dairy-free', label: 'Sin Lactosa' }
  ];

  const templates = [
    {
      id: 'bakery-morning',
      name: 'Panadería - Mañana',
      data: {
        title: 'Bolsa Sorpresa de Panadería',
        originalPrice: '200',
        category: 'bakery',
        description: 'Variedad de panes frescos, pasteles y productos horneados del día',
        pickupTime: '10:00'
      }
    },
    {
      id: 'restaurant-lunch',
      name: 'Restaurante - Almuerzo',
      data: {
        title: 'Comida Casera Sorpresa',
        originalPrice: '150',
        category: 'restaurant',
        description: 'Platos preparados frescos del menú del día',
        pickupTime: '14:00'
      }
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate discounted price (50% off)
      if (field === 'originalPrice' && value) {
        updated.discountedPrice = (parseFloat(value) * 0.5)?.toFixed(0);
      }
      
      return updated;
    });
  };

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      ...template?.data,
      discountedPrice: template?.data?.originalPrice ? 
        (parseFloat(template?.data?.originalPrice) * 0.5)?.toFixed(0) : ''
    }));
  };

  const handleDietaryChange = (value, checked) => {
    setFormData(prev => ({
      ...prev,
      dietaryInfo: checked 
        ? [...prev?.dietaryInfo, value]
        : prev?.dietaryInfo?.filter(item => item !== value)
    }));
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const imageFiles = files?.filter(file => file?.type?.startsWith('image/'));
    const newImages = imageFiles?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      url: URL.createObjectURL(file),
      name: file?.name
    }));
    
    setFormData(prev => ({
      ...prev,
      images: [...prev?.images, ...newImages]?.slice(0, 5) // Max 5 images
    }));
  };

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev?.images?.filter(img => img?.id !== imageId)
    }));
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const publishData = {
      ...formData,
      id: Date.now(),
      publishedAt: new Date(),
      status: 'active'
    };
    
    onPublish(publishData);
    setIsPublishing(false);
    
    // Reset form
    setFormData({
      title: '',
      originalPrice: '',
      discountedPrice: '',
      category: '',
      description: '',
      pickupTime: '',
      quantity: 1,
      dietaryInfo: [],
      images: []
    });
  };

  const isFormValid = formData?.title && formData?.originalPrice && formData?.category && formData?.pickupTime;

  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground">
            Publicación Rápida
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Publica tu bolsa sorpresa en menos de 15 segundos
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Zap" size={20} color="var(--color-accent)" />
          <span className="text-sm font-medium text-accent">Modo Rápido</span>
        </div>
      </div>
      {/* Templates */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          Plantillas Rápidas
        </label>
        <div className="flex flex-wrap gap-2">
          {templates?.map(template => (
            <Button
              key={template?.id}
              variant="outline"
              size="sm"
              onClick={() => handleTemplateSelect(template)}
              className="text-xs"
            >
              <Icon name="FileText" size={14} className="mr-1" />
              {template?.name}
            </Button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <Input
            label="Título de la Bolsa"
            type="text"
            placeholder="Ej: Bolsa Sorpresa de Panadería"
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Precio Original (MXN)"
              type="number"
              placeholder="200"
              value={formData?.originalPrice}
              onChange={(e) => handleInputChange('originalPrice', e?.target?.value)}
              required
            />
            <Input
              label="Precio con Descuento (50%)"
              type="number"
              value={formData?.discountedPrice}
              disabled
              description="Se calcula automáticamente"
            />
          </div>

          <Select
            label="Categoría"
            options={categoryOptions}
            value={formData?.category}
            onChange={(value) => handleInputChange('category', value)}
            placeholder="Selecciona una categoría"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Hora de Recogida"
              type="time"
              value={formData?.pickupTime}
              onChange={(e) => handleInputChange('pickupTime', e?.target?.value)}
              required
            />
            <Input
              label="Cantidad Disponible"
              type="number"
              min="1"
              value={formData?.quantity}
              onChange={(e) => handleInputChange('quantity', parseInt(e?.target?.value))}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Descripción
            </label>
            <textarea
              className="w-full h-24 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              placeholder="Describe brevemente el contenido de la bolsa..."
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />
          </div>

          {/* Dietary Information */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Información Dietética
            </label>
            <div className="grid grid-cols-2 gap-2">
              {dietaryOptions?.map(option => (
                <Checkbox
                  key={option?.value}
                  label={option?.label}
                  checked={formData?.dietaryInfo?.includes(option?.value)}
                  onChange={(e) => handleDietaryChange(option?.value, e?.target?.checked)}
                />
              ))}
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Imágenes (Máx. 5)
            </label>
            <div
              className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                dragActive 
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Icon name="Upload" size={24} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-2">
                Arrastra imágenes aquí o haz clic para seleccionar
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFiles(Array.from(e?.target?.files))}
                className="hidden"
                id="image-upload"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('image-upload')?.click()}
              >
                Seleccionar Archivos
              </Button>
            </div>

            {/* Image Preview */}
            {formData?.images?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {formData?.images?.map(image => (
                  <div key={image?.id} className="relative">
                    <img
                      src={image?.url}
                      alt={`Preview of ${image?.name}`}
                      className="w-16 h-16 object-cover rounded-md border border-border"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => removeImage(image?.id)}
                      className="absolute -top-2 -right-2 w-6 h-6"
                    >
                      <Icon name="X" size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Publish Button */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          <Icon name="Clock" size={16} className="inline mr-1" />
          Tiempo estimado: &lt;15 segundos
        </div>
        <Button
          variant="default"
          onClick={handlePublish}
          disabled={!isFormValid || isPublishing}
          loading={isPublishing}
          className="min-w-32"
        >
          {isPublishing ? 'Publicando...' : 'Publicar Ahora'}
        </Button>
      </div>
    </div>
  );
};

export default QuickPublishForm;